import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Heartbeat } from '@phosphor-icons/react';

const PMH_ITEMS = [
  { key: 'asthma', label: 'Asthma' },
  { key: 'diabetes', label: 'Diabetes' },
  { key: 'cardiac', label: 'Cardiac' },
  { key: 'hypertension', label: 'Hypertension' },
  { key: 'copd', label: 'COPD' },
  { key: 'emphysema', label: 'Emphysema' },
  { key: 'psychiatric', label: 'Psychiatric' },
];

export function MedicalHistorySection({ formData, handleInputChange, handleNestedChange }) {
  const pmh = formData.past_medical_history || {};
  const pmhDisabled = pmh.none || pmh.unable_to_obtain;

  const handlePmhSpecialCheck = (field, checked) => {
    handleNestedChange('past_medical_history', field, checked);
    if (checked) {
      // Clear the other special checkbox and all conditions
      const otherField = field === 'none' ? 'unable_to_obtain' : 'none';
      handleNestedChange('past_medical_history', otherField, false);
      PMH_ITEMS.forEach(item => handleNestedChange('past_medical_history', item.key, false));
      handleNestedChange('past_medical_history', 'other', '');
    }
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl flex items-center gap-2">
          <Heartbeat size={24} className="text-red-600" />
          Medical History
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Allergies */}
          <div className="space-y-3">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Allergies *
            </Label>
            <div className="flex flex-wrap gap-4 mb-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="allergies_nka"
                  checked={formData.allergies_nka}
                  onCheckedChange={(checked) => {
                    handleInputChange('allergies_nka', checked);
                    if (checked) { handleInputChange('allergies', ''); handleInputChange('allergies_unable', false); }
                  }}
                  data-testid="allergies-nka-checkbox"
                />
                <Label htmlFor="allergies_nka" className="text-sm text-slate-700 cursor-pointer">No Known Allergies</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="allergies_unable"
                  checked={formData.allergies_unable}
                  onCheckedChange={(checked) => {
                    handleInputChange('allergies_unable', checked);
                    if (checked) { handleInputChange('allergies', ''); handleInputChange('allergies_nka', false); }
                  }}
                  data-testid="allergies-unable-checkbox"
                />
                <Label htmlFor="allergies_unable" className="text-sm text-slate-700 cursor-pointer">Unable to Obtain</Label>
              </div>
            </div>
            <Input
              value={formData.allergies}
              onChange={(e) => handleInputChange('allergies', e.target.value)}
              placeholder="List known allergies"
              className="rounded-sm border-slate-300"
              disabled={formData.allergies_nka || formData.allergies_unable}
              data-testid="allergies-input"
            />
          </div>

          {/* Current Medication */}
          <div className="space-y-3">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Current Medication *
            </Label>
            <div className="flex flex-wrap gap-4 mb-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="medication_none"
                  checked={formData.medication_none}
                  onCheckedChange={(checked) => {
                    handleInputChange('medication_none', checked);
                    if (checked) { handleInputChange('medication', ''); handleInputChange('medication_unable', false); }
                  }}
                  data-testid="medication-none-checkbox"
                />
                <Label htmlFor="medication_none" className="text-sm text-slate-700 cursor-pointer">No Current Medication</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="medication_unable"
                  checked={formData.medication_unable}
                  onCheckedChange={(checked) => {
                    handleInputChange('medication_unable', checked);
                    if (checked) { handleInputChange('medication', ''); handleInputChange('medication_none', false); }
                  }}
                  data-testid="medication-unable-checkbox"
                />
                <Label htmlFor="medication_unable" className="text-sm text-slate-700 cursor-pointer">Unable to Obtain</Label>
              </div>
            </div>
            <Input
              value={formData.medication}
              onChange={(e) => handleInputChange('medication', e.target.value)}
              placeholder="List current medications"
              className="rounded-sm border-slate-300"
              disabled={formData.medication_none || formData.medication_unable}
              data-testid="medication-input"
            />
          </div>
        </div>

        <div className="space-y-3">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Past Medical History (PMHx) *
          </Label>
          <div className="flex flex-wrap gap-4 mb-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="pmhx_none"
                checked={pmh.none || false}
                onCheckedChange={(checked) => handlePmhSpecialCheck('none', checked)}
                data-testid="pmhx-none-checkbox"
              />
              <Label htmlFor="pmhx_none" className="text-sm text-slate-700 cursor-pointer">None</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="pmhx_unable"
                checked={pmh.unable_to_obtain || false}
                onCheckedChange={(checked) => handlePmhSpecialCheck('unable_to_obtain', checked)}
                data-testid="pmhx-unable-checkbox"
              />
              <Label htmlFor="pmhx_unable" className="text-sm text-slate-700 cursor-pointer">Unable to Obtain</Label>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {PMH_ITEMS.map((item) => (
              <div key={item.key} className="flex items-center space-x-2">
                <Checkbox
                  id={item.key}
                  checked={pmh[item.key] || false}
                  disabled={pmhDisabled}
                  onCheckedChange={(checked) =>
                    handleNestedChange('past_medical_history', item.key, checked)
                  }
                  data-testid={`pmhx-${item.key}-checkbox`}
                />
                <Label htmlFor={item.key} className={`text-sm cursor-pointer ${pmhDisabled ? 'text-slate-400' : 'text-slate-700'}`}>
                  {item.label}
                </Label>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Other Medical History
            </Label>
            <Input
              value={pmh.other || ''}
              onChange={(e) => handleNestedChange('past_medical_history', 'other', e.target.value)}
              placeholder="Other relevant medical history"
              className="rounded-sm border-slate-300"
              disabled={pmhDisabled}
              data-testid="pmhx-other-input"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
